import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { ClientForm } from '@/apps/coach/components/admin/ClientForm';
import { SessionsList } from '@/apps/coach/components/admin/SessionsList';
import { ClientTimeline } from '@/apps/coach/components/admin/ClientTimeline';
import { ReportsList } from '@/apps/coach/components/admin/ReportsList';
import { ClientAppsTab } from '@/apps/coach/components/admin/ClientAppsTab';
import { Button } from '@/components/ui/button';
import { Pencil, Archive, ArchiveRestore, Trash2, ChevronLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { ClientFile } from '@/types/coach';

const TABS = [
  { key: 'overview', label: 'Übersicht' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'timeline', label: 'Zeitstrahl' },
  { key: 'reports', label: 'Berichte' },
  { key: 'apps', label: 'Apps' },
] as const;
type Tab = (typeof TABS)[number]['key'];

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientFile | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.coach.getClient(id)
      .then(setClient)
      .catch((err) => setError(err instanceof Error ? err.message : 'Klient nicht gefunden.'));
  }, [id]);

  async function handleArchiveToggle() {
    if (!client) return;
    try {
      if (client.archived) {
        await api.coach.restoreClient(client.id);
        toast.success('Klient wiederhergestellt.');
      } else {
        await api.coach.archiveClient(client.id);
        toast.success('Klient archiviert.');
      }
      setClient({ ...client, archived: !client.archived });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Aktion fehlgeschlagen.');
    }
  }

  async function handleDelete() {
    if (!client) return;
    const ok = window.confirm(
      `Klient "${client.firstName} ${client.lastName}" wirklich endgültig löschen?\n\n` +
        'Diese Aktion kann nicht rückgängig gemacht werden.',
    );
    if (!ok) return;
    try {
      await api.coach.deleteClient(client.id);
      toast.success('Klient gelöscht.');
      navigate(routes.coach.dashboard(), { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    }
  }

  if (error) {
    return (
      <AdminShell>
        <div className="px-8 py-12 max-w-2xl mx-auto">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(routes.coach.dashboard())}>
            Zurück zur Liste
          </Button>
        </div>
      </AdminShell>
    );
  }
  if (!client) {
    return (
      <AdminShell>
        <div className="px-8 py-12 text-sm text-muted-foreground">Lade…</div>
      </AdminShell>
    );
  }

  const initials = `${client.firstName[0] ?? ''}${client.lastName[0] ?? ''}`.toUpperCase();

  return (
    <AdminShell pageTitle={`${client.firstName} ${client.lastName}`}>
      <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto w-full">
        <button
          type="button"
          onClick={() => navigate(routes.coach.dashboard())}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft size={14} /> Klienten
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-6 pb-4 border-b border-border">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-base font-semibold text-primary shrink-0">
            {initials || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold truncate">
              {client.firstName} {client.lastName}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {client.archived ? (
                <span>Archiviert</span>
              ) : (
                <span className="text-green-600">● Aktiv</span>
              )}{' '}
              · seit {new Date(client.createdAt).toLocaleDateString('de-DE')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="gap-1.5">
              <Pencil size={14} /> Bearbeiten
            </Button>
            <Button size="sm" variant="outline" onClick={handleArchiveToggle} className="gap-1.5">
              {client.archived ? (
                <><ArchiveRestore size={14} /> Wiederherstellen</>
              ) : (
                <><Archive size={14} /> Archivieren</>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDelete}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 size={14} /> Löschen
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-foreground font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <section>
              <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">
                Stammdaten
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex gap-3">
                  <dt className="w-24 text-muted-foreground">E-Mail</dt>
                  <dd>{client.email || '—'}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-24 text-muted-foreground">Telefon</dt>
                  <dd>{client.phone || '—'}</dd>
                </div>
              </dl>
            </section>

            <section>
              <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">Ziel</h3>
              <p className="text-sm whitespace-pre-line">
                {client.goals || (
                  <span className="text-muted-foreground italic">
                    Noch kein Ziel hinterlegt. Über "Bearbeiten" ergänzen.
                  </span>
                )}
              </p>
            </section>

            <section>
              <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">
                Allgemeine Notizen
              </h3>
              <p className="text-sm whitespace-pre-line">
                {client.notes || (
                  <span className="text-muted-foreground italic">Keine Notizen.</span>
                )}
              </p>
            </section>
          </div>
        )}

        {activeTab === 'sessions' && (
          <SessionsList clientId={client.id} clientFirstName={client.firstName} />
        )}

        {activeTab === 'timeline' && (
          <ClientTimeline
            clientId={client.id}
            goal={client.goals}
            createdAt={client.createdAt}
            onCreateSession={() => setActiveTab('sessions')}
          />
        )}

        {activeTab === 'reports' && <ReportsList clientId={client.id} />}

        {activeTab === 'apps' && (
          <ClientAppsTab clientId={client.id} hasEmail={Boolean(client.email)} />
        )}
      </div>

      <ClientForm
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={client}
        onSaved={(saved) => setClient(saved)}
      />
    </AdminShell>
  );
}
